import { useRef, useState, type MouseEvent } from 'react';
import { Button, Text } from '@fluentui/react-components';
import { PopupDialog } from '@vigeo/ui';

/**
 * "Withdraw offer", asking first (Figma `1038:2` card 6, PG3; #502). Withdrawing ends an offer the candidate may be about to
 * sign and cannot be taken back with one click, so a click here only opens the question; nothing is withdrawn until the blue
 * "Withdraw offer" in it is pressed. The keyboard starts on Cancel, so pressing Enter changes nothing.
 *
 * ONE BUTTON FOR EVERY PLACE THAT WITHDRAWS. The offer screens (waiting, sent back), the Send offer footer and the older offers
 * list each had their own button that fired the command straight from the click; they all use this now, so a fifth place cannot
 * forget to ask.
 */
export function WithdrawOfferButton({ candidateName, label = 'Withdraw offer', pendingLabel, pending = false, disabled = false, appearance = 'subtle', onConfirm }: {
  /** Whom the offer is for, in the sentence that says what will happen. */
  readonly candidateName: string;
  /** What the opening button says. The blue button in the question always says "Withdraw offer". */
  readonly label?: string;
  /** What the opening button says while the withdrawal is under way. */
  readonly pendingLabel?: string;
  readonly pending?: boolean;
  readonly disabled?: boolean;
  readonly appearance?: 'subtle' | 'secondary' | 'primary';
  /** Called once, when the blue button is pressed. */
  readonly onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const ask = (event: MouseEvent<HTMLElement>) => { opener.current = event.currentTarget; setAsking(true); };
  return (
    <>
      <Button appearance={appearance} disabled={disabled || pending || asking} onClick={ask}>{pending && pendingLabel !== undefined ? pendingLabel : label}</Button>
      <PopupDialog
        open={asking}
        title="Withdraw this offer?"
        startOn="cancel"
        opener={opener}
        onClose={() => setAsking(false)}
        primary={{ label: 'Withdraw offer', onClick: () => { setAsking(false); onConfirm(); } }}
      >
        <Text>The offer for {candidateName} will be withdrawn.</Text>
      </PopupDialog>
    </>
  );
}
